import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck, X } from "lucide-react";

// Client-only consent record. SECURITY NOTE: contains only the
// visitor's marketing opt-in boolean + a timestamp — no PII, no auth.
// localStorage is the correct place for this per GDPR/PIPEDA guidance
// (the value must be visible/deletable by the user and outlive session
// cookies). Do NOT migrate this to server cookies.
const STORAGE_KEY = "toa_consent_v1";

function readConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(marketing) {
  const payload = { marketing, timestamp: new Date().toISOString(), version: 1 };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch { /* ignore */ }
  return payload;
}

/**
 * Bottom-left cookie / marketing consent banner.
 *
 * Complies with PIPEDA + Quebec Law 25 informed-opt-in:
 *   - Shown ONLY when no prior choice exists.
 *   - Accept → writes {marketing:true} to localStorage + fires
 *     `window.__toaInitTracking()` (defined in public/index.html) to load
 *     GTM + Meta Pixel on the fly.
 *   - Decline → writes {marketing:false}; tracking never loads.
 *   - "Learn more" links out to /privacy (page can be added later; safe
 *     to link now).
 */
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return; // Suppress on admin routes — no marketing tracking there.
    // Delay showing so the banner doesn't fight the LCP.
    const id = setTimeout(() => {
      if (readConsent() === null) setVisible(true);
    }, 1200);
    return () => clearTimeout(id);
  }, [isAdmin]);

  // Reserve safe space at the bottom of the viewport while the banner is
  // visible so it never overlaps page content (LOW-severity finding from
  // UAT iteration 14 on /installation-remodeling desktop). Applied via a
  // body attribute + a CSS rule in index.css so React does not need to
  // touch layout on every route.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (visible) {
      document.body.setAttribute("data-consent-visible", "true");
    } else {
      document.body.removeAttribute("data-consent-visible");
    }
    return () => document.body.removeAttribute("data-consent-visible");
  }, [visible]);

  const accept = () => {
    writeConsent(true);
    if (typeof window !== "undefined" && typeof window.__toaInitTracking === "function") {
      window.__toaInitTracking();
    }
    setVisible(false);
  };

  const decline = () => {
    writeConsent(false);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          data-testid="consent-banner"
          role="dialog"
          aria-label="Cookie and marketing consent"
          className="fixed left-4 right-4 md:left-auto md:right-6 bottom-4 md:bottom-6 z-[70] md:max-w-[380px] max-w-[520px] bg-[#0A0A0A] text-[#F5F2EB] border border-[#FF6A00]/60 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)]"
        >
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 flex items-center justify-center bg-[#FF6A00]/15 border border-[#FF6A00]/40">
                <Cookie size={18} className="text-[#FF6A00]" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg md:text-xl leading-tight text-[#F5F2EB]" data-testid="consent-banner-title">
                  We value your privacy.
                </p>
                <p className="mt-2 text-[13px] leading-relaxed text-[#F5F2EB]/80">
                  We use essential cookies to run this site. With your consent, we also use analytics + advertising cookies (Google, Meta) to improve your experience and measure our campaigns. You can change your choice anytime — just clear this site&apos;s data.
                </p>
              </div>
              <button
                type="button"
                onClick={decline}
                aria-label="Dismiss and decline marketing cookies"
                data-testid="consent-banner-close"
                className="shrink-0 -mt-1 -mr-1 p-1 text-[#F5F2EB]/60 hover:text-[#F5F2EB]"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={accept}
                data-testid="consent-accept"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FF6A00] hover:bg-[#FF8A33] text-[#0A0A0A] text-[12px] tracking-[0.16em] uppercase font-bold transition-colors"
              >
                <ShieldCheck size={14} aria-hidden="true" />
                Accept All
              </button>
              <button
                type="button"
                onClick={decline}
                data-testid="consent-decline"
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-transparent border border-[#F5F2EB]/40 hover:border-[#F5F2EB] text-[#F5F2EB] text-[12px] tracking-[0.16em] uppercase font-medium transition-colors"
              >
                Decline
              </button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
